import { useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Global flag to prevent duplicate processing across component remounts
let isProcessingAuth = false;

export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessedRef = useRef(false);

  const processAuth = useCallback(async () => {
    // Prevent double processing (React Strict Mode protection)
    if (isProcessingAuth || hasProcessedRef.current) {
      return;
    }
    
    try {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/login", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];
      
      // Check if this session_id was already processed (prevents duplicate calls)
      const processedSessionId = sessionStorage.getItem("processed_session_id");
      if (processedSessionId === sessionId) {
        navigate("/login", { replace: true });
        return;
      }

      // Set flags BEFORE making API call to prevent race conditions
      isProcessingAuth = true;
      hasProcessedRef.current = true;
      sessionStorage.setItem("processed_session_id", sessionId);

      // Clear the hash immediately to prevent duplicate processing
      window.history.replaceState(null, "", window.location.pathname);

      // Exchange session_id for session data via backend
      const response = await axios.post(`${API}/auth/session`, {
        session_id: sessionId
      }, {
        withCredentials: true  // CRITICAL: Ensure cookies are sent/received
      });

      const userData = response.data;

      // Capture the session_token for the Bearer fallback (cookie may be dropped in
      // some production contexts), then strip it from the cached user object.
      const { session_token: sessionToken, ...userOnly } = userData;
      if (sessionToken) {
        localStorage.setItem("session_token", sessionToken);
        axios.defaults.headers.common["Authorization"] = `Bearer ${sessionToken}`;
      }

      // Store user in localStorage for quick access
      localStorage.setItem("user", JSON.stringify(userOnly));

      // Clear the processed session id
      sessionStorage.removeItem("processed_session_id");
      
      // Reset flag before navigation
      isProcessingAuth = false;
      
      // Navigate to dashboard with user data to skip auth check
      navigate("/", { state: { user: userOnly }, replace: true });
    } catch (error) {
      sessionStorage.removeItem("processed_session_id");
      isProcessingAuth = false;
      hasProcessedRef.current = false;
      
      // Check for domain restriction error
      const errorMessage = error.response?.data?.detail || "";
      if (error.response?.status === 403 || errorMessage.includes("Wellversed")) {
        sessionStorage.setItem("auth_error", errorMessage || "Access restricted to Wellversed users only");
      }
      
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    processAuth();
  }, [processAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
